import React, { useState, useEffect } from 'react';
import BeatLoader from 'react-spinners/BeatLoader';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../../app/constants'

function MockExamResults() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [diagnosticData, setDiagnosticData] = useState(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const navigate = useNavigate();

    useEffect(() => {
        // Add fullscreen classes to body when component mounts
        document.body.classList.add('diagnostic-stats-fullscreen');
        document.body.classList.add('review-personalized-fullscreen');
        fetchDiagnosticData();
        
        // Add resize listener for responsive design
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        window.addEventListener('resize', handleResize);
        
        // Add custom style to override any potential purple highlighting
        const style = document.createElement('style');
        style.innerHTML = `
            .progress-value, 
            .stat-value, 
            .metric-value,
            [class*="progress-value"],
            [class*="stat-value"],
            [class*="metric-value"] {
                color: #111827 !important;
            }
        `;
        document.head.appendChild(style);

        // Clean up function to remove the classes and style when component unmounts
        return () => {
            document.body.classList.remove('diagnostic-stats-fullscreen');
            document.body.classList.remove('review-personalized-fullscreen');
            window.removeEventListener('resize', handleResize);
            document.head.removeChild(style);
        };
    }, []);

    const fetchDiagnosticData = async () => {
        try {
            const jwt = localStorage.getItem('jwt');
            if (!jwt) {
                throw new Error("JWT token not found in localStorage");
            }
            const response = await fetch(`${API_URL}/api/v1/mock-exam/analytics`, {
                headers: { 'Authorization': `Bearer ${jwt}` }
            });
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            console.log(data);
            setDiagnosticData(data);
        } catch (error) {
            console.error("Error fetching mock exam data:", error);
            setError("Error fetching mock exam data");
        } finally {
            setLoading(false);
        }
    };

    const formatText = (text) => {
        return text.charAt(0).toUpperCase() + text.slice(1);
    };

    // Loading state
    if (loading) {
        return (
            <div style={{
                height: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#fff'
            }}>
                <BeatLoader color="#9334E9" />
                <p style={{marginTop: '16px', color: '#6B7280', fontFamily: 'Manrope, sans-serif'}}>
                    Loading your mock exam results...
                </p>
            </div>
        );
    }
    
    // Error state
    if (error) {
        return (
            <div style={{
                height: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '24px',
                backgroundColor: '#fff',
                fontFamily: 'Manrope, sans-serif'
            }}>
                <div style={{fontSize: '20px', color: '#EF4444', marginBottom: '8px'}}>
                    Unable to load your mock exam data
                </div>
                <p style={{color: '#6B7280', marginBottom: '24px', textAlign: 'center'}}>
                    {error}
                </p>
                <button 
                    onClick={() => navigate('/mock-exam-review')} 
                    style={{
                        padding: '10px 16px',
                        background: '#9334E9',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        fontWeight: '600'
                    }}
                >
                    Return to Review
                </button>
            </div>
        );
    }

    // Calculate if each section is passed (>= 50%)
    const sectionsPassed = Object.values(diagnosticData.detailed_analysis.section_scores).every(
        section => section.score_percentage >= 50
    );

    return (
        <div className="analytics review-personalized-fullscreen" style={{
            fontFamily: "'Manrope', sans-serif",
            background: 'white',
            overflowY: 'auto',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            width: '100vw',
            minHeight: '100vh',
            padding: isMobile ? '16px 12px' : '20px 16px'
        }}>
            {/* Header with title and desktop buttons */}
            <div className="analytics-header" style={{
                marginBottom: '24px',
                maxWidth: '1000px',
                margin: '0 auto 24px auto',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexDirection: window.innerWidth <= 768 ? 'column' : 'row'
            }}>
                <h1 style={{
                    margin: 0,
                    fontSize: window.innerWidth <= 768 ? '24px' : '28px',
                    fontWeight: '700',
                    fontFamily: 'Manrope, sans-serif',
                    color: '#111827',
                    lineHeight: '1.3',
                    textAlign: window.innerWidth <= 768 ? 'center' : 'left'
                }}>
                    Mock Exam Analysis
                </h1>
                
                {/* Desktop action button */}
                {window.innerWidth > 768 && (
                    <div style={{
                        display: 'flex',
                        gap: '12px'
                    }}>
                        <button
                            onClick={() => navigate('/')}
                            style={{
                                padding: '14px 20px',
                                borderRadius: '12px',
                                border: 'none',
                                background: '#9334E9',
                                color: 'white',
                                fontWeight: '600',
                                fontSize: '15px',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                                minHeight: '48px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontFamily: 'Manrope, sans-serif',
                                transition: 'all 0.2s ease',
                                boxShadow: '0 2px 4px rgba(147, 52, 233, 0.2)'
                            }}
                        >
                            Go to Dashboard
                        </button>
                    </div>
                )}
            </div>
            
            {/* Information banner about study plan generation */}
            <div style={{
                backgroundColor: '#F5F3FF',
                borderRadius: '16px',
                padding: window.innerWidth <= 768 ? '24px 20px' : '32px 28px',
                border: '1px solid #E9D5FF',
                color: '#6B7280',
                lineHeight: '1.6',
                maxWidth: '1000px',
                margin: '0 auto 32px auto',
                fontFamily: 'Manrope, sans-serif',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.02)',
                textAlign: 'center'
            }}>
                <div style={{
                    fontWeight: '700',
                    color: '#9334E9',
                    marginBottom: '12px',
                    fontSize: window.innerWidth <= 768 ? '18px' : '20px',
                    fontFamily: 'Manrope, sans-serif',
                    lineHeight: '1.3'
                }}>
                    Your Mock Exam Results are Ready
                </div>
                <p style={{margin: '0 0 16px 0', fontSize: window.innerWidth <= 768 ? '14px' : '15px', fontFamily: 'Manrope, sans-serif', lineHeight: '1.5'}}>
                    Based on your mock exam performance, we've identified your strengths and areas for improvement.
                </p>
                <p style={{margin: '0', fontSize: window.innerWidth <= 768 ? '14px' : '15px', fontFamily: 'Manrope, sans-serif', lineHeight: '1.5', opacity: '0.9'}}>
                    Use these insights to focus your study efforts and maximize your JLPT preparation progress.
                </p>
            </div>

            {/* Mobile action button only */}
            {window.innerWidth <= 768 && (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                    maxWidth: '1000px',
                    margin: '0 auto 48px auto'
                }}>
                    <button
                        onClick={() => navigate('/')}
                        style={{
                            padding: '14px 20px',
                            borderRadius: '12px',
                            border: 'none',
                            background: '#9334E9',
                            color: 'white',
                            fontWeight: '600',
                            fontSize: '15px',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                            minHeight: '48px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontFamily: 'Manrope, sans-serif',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 2px 4px rgba(147, 52, 233, 0.2)'
                        }}
                    >
                        Go to Dashboard
                    </button>
                </div>
            )}
            
            {/* Performance Summary Section */}
            <div style={{
                backgroundColor: '#FFFFFF',
                borderRadius: '16px',
                padding: window.innerWidth <= 768 ? '24px 20px' : '28px 24px',
                border: '1px solid #E5E7EB',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                maxWidth: '1000px',
                margin: '0 auto 32px auto'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '24px'
                }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9334E9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 20V10"/>
                        <path d="M18 20V4"/>
                        <path d="M6 20v-4"/>
                    </svg>
                    <h2 style={{
                        margin: 0,
                        fontSize: window.innerWidth <= 768 ? '18px' : '20px',
                        fontWeight: '600',
                        fontFamily: 'Manrope, sans-serif',
                        color: '#111827'
                    }}>
                        Performance Summary
                    </h2>
                </div>
                
                {/* Grid for overall performance */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : '1fr 1fr',
                    gap: window.innerWidth <= 768 ? '16px' : '24px',
                    marginBottom: '20px'
                }}>
                    <div style={{
                        textAlign: 'center',
                        padding: '20px',
                        backgroundColor: '#F5F3FF',
                        borderRadius: '8px'
                    }}>
                        <div style={{
                            fontSize: '13px',
                            fontWeight: '500',
                            color: '#6B7280',
                            marginBottom: '8px',
                            fontFamily: 'Manrope, sans-serif'
                        }}>
                            Overall Score
                        </div>
                        <div style={{
                            fontSize: window.innerWidth <= 768 ? '28px' : '32px',
                            fontWeight: '700',
                            color: '#9334E9',
                            marginBottom: '8px',
                            fontFamily: 'Manrope, sans-serif'
                        }}>
                            {diagnosticData.performance.overall_score_percentage}%
                        </div>
                        <div style={{
                            fontSize: '13px',
                            color: '#6B7280',
                            fontFamily: 'Manrope, sans-serif'
                        }}>
                            {diagnosticData.performance.total_correct} of {diagnosticData.performance.total_questions} questions
                        </div>
                    </div>
                    
                    <div style={{
                        textAlign: 'center',
                        padding: '20px',
                        backgroundColor: '#F5F3FF',
                        borderRadius: '8px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}>
                        <div style={{
                            fontSize: '13px',
                            fontWeight: '500',
                            color: '#6B7280',
                            marginBottom: '8px',
                            fontFamily: 'Manrope, sans-serif'
                        }}>
                            Status
                        </div>
                        <div style={{
                            backgroundColor: sectionsPassed ? '#DCFCE7' : '#F3E8FF',
                            color: sectionsPassed ? '#10B981' : '#7C3AED',
                            padding: '6px 12px',
                            borderRadius: '12px',
                            fontSize: '14px',
                            fontWeight: '600',
                            marginBottom: '8px'
                        }}>
                            {sectionsPassed ? 'PASS' : 'Needs some work'}
                        </div>
                        {!sectionsPassed && (
                            <div style={{
                                fontSize: '12px',
                                color: '#6B7280',
                                fontStyle: 'italic',
                                textAlign: 'center',
                                lineHeight: '1.4',
                                marginTop: '4px'
                            }}>
                                No worries! Keep practicing with targeted study sessions.
                            </div>
                        )}
                    </div>
                </div>
                
                {/* Section scores */}
                <div style={{
                    fontWeight: '600', 
                    fontSize: window.innerWidth <= 768 ? '16px' : '18px', 
                    marginBottom: '16px',
                    fontFamily: 'Manrope, sans-serif',
                    color: '#111827'
                }}>
                    Section Performance
                </div>
                
                <style>{`
                    .section-score {
                        color: #111827 !important;
                        font-weight: 600 !important;
                        font-size: 16px !important;
                    }
                `}</style>
                
                {Object.entries(diagnosticData.detailed_analysis.section_scores).map(([section, { score_percentage }], index) => (
                    <div key={index} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '12px 0',
                        borderBottom: index === Object.entries(diagnosticData.detailed_analysis.section_scores).length - 1 ? 'none' : '1px solid #E5E7EB'
                    }}>
                        <div style={{
                            fontSize: window.innerWidth <= 768 ? '14px' : '15px',
                            fontFamily: 'Manrope, sans-serif',
                            color: '#374151',
                            fontWeight: '500'
                        }}>
                            {formatText(section)}
                        </div>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}>
                            <span className="section-score">
                                {score_percentage}%
                            </span>
                            <div style={{
                                backgroundColor: score_percentage >= 50 ? '#DCFCE7' : '#F3E8FF',
                                color: score_percentage >= 50 ? '#10B981' : '#7C3AED',
                                padding: '2px 6px',
                                borderRadius: '10px',
                                fontSize: '11px',
                                fontWeight: '600'
                            }}>
                                {score_percentage >= 50 ? 'PASS' : 'IMPROVE'}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
            
            {/* Recommendations */}
            <div style={{
                backgroundColor: 'white',
                border: '1px solid #E5E7EB',
                borderRadius: '12px',
                padding: '20px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                maxWidth: '1000px',
                margin: '0 auto 24px auto'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '16px'
                }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9334E9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                    </svg>
                    <h2 style={{
                        margin: 0,
                        fontSize: window.innerWidth <= 768 ? '16px' : '18px',
                        fontWeight: '600',
                        fontFamily: 'Manrope, sans-serif',
                        color: '#111827'
                    }}>
                        Recommendations
                    </h2>
                </div>
                
                <div style={{
                    color: '#6B7280', 
                    lineHeight: '1.5', 
                    marginBottom: '20px',
                    fontSize: window.innerWidth <= 768 ? '14px' : '15px',
                    fontFamily: 'Manrope, sans-serif'
                }}>
                    Based on your mock exam results, we recommend:
                </div>
                
                {/* Main recommendation box */}
                <div style={{
                    backgroundColor: '#F5F3FF',
                    borderRadius: '12px',
                    padding: '20px',
                    marginBottom: '16px',
                    border: '1px solid #E5E7EB'
                }}>
                    <div style={{
                        fontSize: window.innerWidth <= 768 ? '16px' : '18px',
                        fontWeight: '600',
                        marginBottom: '12px',
                        color: '#9334E9',
                        fontFamily: 'Manrope, sans-serif'
                    }}>
                        {sectionsPassed ? 'Continue your study plan' : 'Review key concepts'}
                    </div>
                    <div style={{
                        fontSize: window.innerWidth <= 768 ? '14px' : '15px', 
                        color: '#6B7280',
                        marginBottom: '20px',
                        lineHeight: '1.5',
                        fontFamily: 'Manrope, sans-serif'
                    }}>
                        {sectionsPassed 
                            ? 'You\'re on the right track. Keep practicing to improve your score further.'
                            : 'Focus on the areas below to strengthen your understanding and improve your performance.'}
                    </div>

                    {/* Areas to Focus On - integrated */}
                    {diagnosticData.detailed_analysis.weaknesses && diagnosticData.detailed_analysis.weaknesses.length > 0 && (
                        <div>
                            <div style={{
                                fontSize: window.innerWidth <= 768 ? '14px' : '15px',
                                fontWeight: '600',
                                marginBottom: '12px',
                                color: '#9334E9',
                                fontFamily: 'Manrope, sans-serif'
                            }}>
                                Focus Areas
                            </div>
                            
                            <div style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '8px',
                                marginBottom: '12px'
                            }}>
                                {diagnosticData.detailed_analysis.weaknesses.map((weakness, index) => (
                                    <span key={index} style={{
                                        display: 'inline-block',
                                        backgroundColor: '#FFFFFF',
                                        color: '#9334E9',
                                        padding: window.innerWidth <= 768 ? '10px 16px' : '12px 18px',
                                        borderRadius: '20px',
                                        fontSize: window.innerWidth <= 768 ? '14px' : '15px',
                                        fontWeight: '500',
                                        border: '1px solid rgba(255,255,255,0.3)',
                                        fontFamily: 'Manrope, sans-serif'
                                    }}>
                                        {formatText(weakness)}
                                    </span>
                                ))}
                            </div>

                            {/* Priority message */}
                            {!sectionsPassed && (
                                <div style={{
                                    padding: '12px 16px',
                                    backgroundColor: '#F5F3FF',
                                    borderRadius: '8px',
                                    border: '1px solid #E9D5FF',
                                    fontSize: window.innerWidth <= 768 ? '13px' : '14px',
                                    color: '#7C3AED',
                                    fontFamily: 'Manrope, sans-serif',
                                    lineHeight: '1.4'
                                }}>
                                    Start with {formatText(diagnosticData.detailed_analysis.weaknesses[0])}
                                    {diagnosticData.detailed_analysis.weaknesses.length > 1 ? ` and ${formatText(diagnosticData.detailed_analysis.weaknesses[1])}` : ''} for maximum impact
                                </div>
                            )}
                        </div>
                    )}

                    {/* Success message when no specific weaknesses */}
                    {(!diagnosticData.detailed_analysis.weaknesses || diagnosticData.detailed_analysis.weaknesses.length === 0) && (
                        <div style={{
                            backgroundColor: '#F0FDF4',
                            border: '1px solid #86EFAC',
                            borderRadius: '8px',
                            padding: '16px',
                            textAlign: 'center'
                        }}>
                            <div style={{
                                fontSize: window.innerWidth <= 768 ? '15px' : '16px',
                                fontWeight: '600',
                                color: '#15803D',
                                marginBottom: '4px',
                                fontFamily: 'Manrope, sans-serif'
                            }}>
                                Great performance!
                            </div>
                            <div style={{
                                fontSize: window.innerWidth <= 768 ? '14px' : '15px',
                                color: '#166534',
                                lineHeight: '1.4',
                                fontFamily: 'Manrope, sans-serif'
                            }}>
                                {sectionsPassed ? 'You scored well across all sections. Keep up the excellent work!' : 'Complete more exercises to identify specific areas for improvement'}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default MockExamResults;